import useFeatureFlags, { getFieldGroupFlag } from "hooks/useFeatureFlags";
import useProperty from "hooks/useProperty";

import { WellDetailCardData } from "api/data";

import { EntityKind } from "models/entityKind";

import { TYPED_NA_VALUES } from "components/user-defined-columns/utils/userDefinedColumnUtils";

import { MISSING_VALUE, StatCell, StatLabel, StatValue } from "./styles";

/**
 * Pulls the trailing "(unit)" off a catalogue title. The unit is written into
 * the title client-side from the user's unit setting, so it is the only
 * correct source for a unit string.
 */
export function splitUnitFromTitle(title: Maybe<string>): {
  label: string;
  unit: string;
} {
  if (!title) return { label: "", unit: "" };
  const match = title.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (!match) return { label: title, unit: "" };
  return { label: match[1], unit: match[2] };
}

const withUnit = (label: string, unit: string): string =>
  unit ? `${label} (${unit})` : label;

const TRUE_VALUES = new Set(["true", "y", "yes", "1"]);
const FALSE_VALUES = new Set(["false", "n", "no", "0"]);

/**
 * A field with no value does not arrive empty. The well-detail response is
 * formatted server-side, so it arrives as the literal the formatter writes —
 * "n/a" — which renders as data unless it is read as an absence:
 * `LifecycleTimeline` filled a milestone's dot in as though the date had
 * happened. Matched on the whole trimmed value against the repo's own list of
 * the server's NA family, case-insensitively, plus the hyphen the panel itself
 * draws for a missing value.
 */
export function isMissingPanelValue(raw: Maybe<string | Blob>): boolean {
  if (typeof raw !== "string") return true;
  const trimmed = raw.trim().toLowerCase();
  return trimmed === "" || trimmed === MISSING_VALUE || TYPED_NA_VALUES.includes(trimmed);
}

const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

// Groups the whole part only, so the server's sig-fig rounding of the
// fraction is kept as sent.
function groupThousands(value: string): string {
  const trimmed = value.trim();
  if (!PLAIN_NUMBER.test(trimmed)) return value;
  const [whole, fraction] = trimmed.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
}

/**
 * Booleans arrive as text and render as Y / N, as the Well Ticket does, unless
 * the field names its own text for true and false.
 * Counts arrive from the same server-side formatter as "1.0000000", so they
 * are rounded back to the whole wells they count. A value the parse cannot
 * read is left as it came, rather than shown as NaN.
 * Numbers arrive without thousands separators, so commas are added as Insights
 * shows them.
 */
export function formatPanelValue(
  raw: Maybe<string | Blob>,
  isBoolean = false,
  isInteger = false,
  booleanText: readonly [string, string] = ["Y", "N"]
): Maybe<string> {
  if (isMissingPanelValue(raw)) return null;
  if (typeof raw !== "string") return null;
  if (isInteger) {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? groupThousands(String(Math.round(parsed))) : raw;
  }
  if (!isBoolean) return groupThousands(raw);
  const normalized = raw.trim().toLowerCase();
  if (TRUE_VALUES.has(normalized)) return booleanText[0];
  if (FALSE_VALUES.has(normalized)) return booleanText[1];
  return raw;
}

/**
 * Reports whether the org has the field group behind `propertyGroup` turned
 * on. `useProperty` reads the unfiltered catalogue, so without this check the
 * panel would label and render fields the org does not have.
 */
export function useIsFieldGroupEnabled(propertyGroup: Maybe<string>): boolean {
  const { hasFeature } = useFeatureFlags();
  if (!propertyGroup) return true;
  const flag = getFieldGroupFlag(propertyGroup);
  return !flag || hasFeature(flag);
}

export interface PanelFieldProps {
  propertyName: string;
  /** Used when the catalogue has no entry for this property. */
  fallbackLabel: string;
  wellData: Maybe<WellDetailCardData>;
  isBoolean?: boolean;
  isInteger?: boolean;
  /** Replaces the catalogue title. The catalogue's unit is still appended. */
  label?: string;
  booleanText?: readonly [string, string];
}

/**
 * One label-over-value cell. Two absent-data cases behave oppositely: a
 * flag-disabled group drops the cell, anything else keeps the layout and shows
 * a hyphen.
 */
export function PanelField({
  propertyName,
  fallbackLabel,
  wellData,
  isBoolean = false,
  isInteger = false,
  label: labelOverride,
  booleanText
}: PanelFieldProps): JSX.Element {
  const property = useProperty(propertyName, false, "", EntityKind.Well);
  const groupEnabled = useIsFieldGroupEnabled(property?.group);

  if (!groupEnabled) return null;

  const label = labelOverride
    ? withUnit(labelOverride, splitUnitFromTitle(property?.title).unit)
    : property?.fullContextTitle ?? fallbackLabel;
  const value = formatPanelValue(
    wellData?.[propertyName],
    isBoolean,
    isInteger,
    booleanText
  );

  return (
    <StatCell title={label}>
      <StatLabel>{label}</StatLabel>
      <StatValue $missing={!value}>{value ?? MISSING_VALUE}</StatValue>
    </StatCell>
  );
}
